import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    Typography,
    Alert,
} from "@mui/material";
import { DialogHeader } from "../../../../auth/components/DialogHeader";
import SelectInuputComponent, {
    SelectInputOptions,
} from "../../../../main/components/SelectInputComponent";
import { useState, useMemo } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { GenerationType, TacticalInterval } from "../../types";

interface TacticalFiltersModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    selectedAtivo: GenerationType | undefined;
    setSelectedAtivo: (ativos: GenerationType | undefined) => void;
    selectedInterval: TacticalInterval;
    setSelectedInterval: (interval: TacticalInterval) => void;
    selectedStartDate: Dayjs | null;
    setSelectedStartDate: (date: Dayjs | null) => void;
    selectedEndDate: Dayjs | null;
    setSelectedEndDate: (date: Dayjs | null) => void;
    selectedWeeklyDate: Dayjs | null;
    setSelectedWeeklyDate: (date: Dayjs | null) => void;
    selectedMonthlyDate: Dayjs | null;
    setSelectedMonthlyDate: (date: Dayjs | null) => void;
}

export default function TacticalFiltersModal(
    props: TacticalFiltersModalProps
) {
    const {
        open,
        setOpen,
        selectedAtivo,
        setSelectedAtivo,
        selectedInterval,
        setSelectedInterval,
        selectedStartDate,
        setSelectedStartDate,
        selectedEndDate,
        setSelectedEndDate,
        selectedWeeklyDate,
        setSelectedWeeklyDate,
        selectedMonthlyDate,
        setSelectedMonthlyDate,
    } = props;

    const [tempSelectedAtivo, setTempSelectedAtivo] = useState<
        GenerationType | undefined
    >(selectedAtivo);
    const [tempSelectedInterval, setTempSelectedInterval] =
        useState<TacticalInterval>(selectedInterval);
    const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(
        selectedStartDate
    );
    const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(
        selectedEndDate
    );
    const [tempWeeklyDate, setTempWeeklyDate] = useState<Dayjs | null>(
        selectedWeeklyDate
    );
    const [tempMonthlyDate, setTempMonthlyDate] = useState<Dayjs | null>(
        selectedMonthlyDate
    );

    /*     const ativosOptions: SelectInputOptions[] = [
            { label: "Todos Ativos", value: undefined },
            { label: "Solar", value: GenerationType.Solar },
            { label: "Hídrica", value: GenerationType.Hidrica },
        ]; */

    const intervalsOptions: SelectInputOptions[] = [
        { label: "Diário", value: TacticalInterval.Daily },
        { label: "Semanal", value: TacticalInterval.Weekly },
        { label: "Mensal", value: TacticalInterval.Monthly },
        { label: "Período Personalizado", value: TacticalInterval.Custom },
    ];

    const validationError = useMemo(() => {
        if (tempSelectedInterval === TacticalInterval.Custom) {
            if (!tempStartDate || !tempEndDate) {
                return "Data inicial e final são obrigatórias";
            }

            if (tempStartDate.isAfter(tempEndDate)) {
                return "A data inicial não pode ser posterior à data final";
            }

            const diffDays = tempEndDate.diff(tempStartDate, "day");
            if (diffDays > 365) {
                return "O período não pode exceder 1 ano";
            }
        }

        return null;
    }, [tempSelectedInterval, tempStartDate, tempEndDate]);

    const onClose = () => {
        setOpen(false);
        setTempSelectedAtivo(selectedAtivo);
        setTempSelectedInterval(selectedInterval);
        setTempStartDate(selectedStartDate);
        setTempEndDate(selectedEndDate);
        setTempWeeklyDate(selectedWeeklyDate);
        setTempMonthlyDate(selectedMonthlyDate);
    };

    const handleConfirm = () => {
        if (validationError) {
            return;
        }

        setSelectedAtivo(tempSelectedAtivo);
        setSelectedInterval(tempSelectedInterval);

        if (tempSelectedInterval === TacticalInterval.Custom) {
            setSelectedStartDate(tempStartDate);
            setSelectedEndDate(tempEndDate);
        } else if (tempSelectedInterval === TacticalInterval.Weekly) {
            setSelectedWeeklyDate(tempWeeklyDate);
        } else if (tempSelectedInterval === TacticalInterval.Monthly) {
            setSelectedMonthlyDate(tempMonthlyDate);
        }

        setOpen(false);
    };

    const renderDatePickers = () => {
        const today = dayjs();

        switch (tempSelectedInterval) {
            case TacticalInterval.Daily:
                return null;

            case TacticalInterval.Weekly:
                return (
                    <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Segunda-feira da semana"
                                value={tempWeeklyDate}
                                onChange={(newDate) => setTempWeeklyDate(newDate)}
                                format="DD/MM/YYYY"
                                maxDate={today}
                                shouldDisableDate={(date) => {
                                    return date.day() !== 1;
                                }}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                        helperText: "Selecione apenas segundas-feiras"
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                );

            case TacticalInterval.Monthly:
                return (
                    <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Mês e Ano"
                                value={tempMonthlyDate}
                                onChange={(newDate) => setTempMonthlyDate(newDate)}
                                views={["year", "month"]}
                                format="MM/YYYY"
                                maxDate={today}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                );

            case TacticalInterval.Custom:
                return (
                    <>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Data inicial"
                                    value={tempStartDate}
                                    onChange={(newDate) => setTempStartDate(newDate)}
                                    maxDate={tempEndDate ? (tempEndDate.isAfter(today) ? today : tempEndDate) : today}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Data final"
                                    value={tempEndDate}
                                    onChange={(newDate) => setTempEndDate(newDate)}
                                    minDate={tempStartDate ? tempStartDate : undefined}
                                    maxDate={today}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            onClose={onClose}
            aria-labelledby="tactical-filters-dialog-title"
            open={open}
            sx={{
                "& .MuiDialog-paper": {
                    width: "100%",
                    maxWidth: "500px",
                },
            }}
        >
            <DialogHeader id="tactical-filters-dialog-title" onClose={onClose}>
                <Typography sx={{ fontSize: "20px", fontWeight: 500 }}>
                    Filtros
                </Typography>
            </DialogHeader>
            <DialogContent sx={{ marginY: "8px" }} dividers>
                <Grid container spacing={2}>
                    {/*                     <Grid item xs={12} sm={6}>
                        <SelectInuputComponent
                            options={ativosOptions}
                            setFilter={setTempSelectedAtivo}
                            defaultValue={tempSelectedAtivo}
                            fullWidth={true}
                            sx={{ height: "35px" }}
                        />
                    </Grid> */}
                    <Grid item xs={12}>
                        <SelectInuputComponent
                            options={intervalsOptions}
                            setFilter={setTempSelectedInterval}
                            defaultValue={tempSelectedInterval}
                            fullWidth={true}
                            sx={{ height: "35px" }}
                        />
                    </Grid>

                    {renderDatePickers()}

                    {validationError && (
                        <Grid item xs={12}>
                            <Alert severity="error" sx={{ fontSize: "13px" }}>
                                {validationError}
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ padding: "16px" }}>
                <Button onClick={onClose} variant="outlined">
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={validationError !== null}
                >
                    Aplicar Filtros
                </Button>
            </DialogActions>
        </Dialog>
    );
}